import {
  DateRangeOption,
  getDateRangeName,
  getFormattedDate,
  makeDateRanges,
} from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import { useSearchParam } from 'components/search/search.hooks';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import FilterChip from './filter-chip.component';

const DateRangeFilterChip = ({
  openFilterDrawer,
  name,
  startParamName,
  endParamName,
  showName = false,
}) => {
  const {
    value: startValue,
    reset: resetStart,
    searchParam: { toUrlString: startTo, defaultValue: startDefault, filter },
  } = useSearchParam<DateTime>(startParamName);
  const { clearable } = filter;
  // Preset-only date filters (e.g. Guest Journey) show the preset name on the
  // chip instead of the raw range; other date chips keep the formatted range.
  const { presetsOnly, predefinedDateOptions, anchorDate } = filter as {
    presetsOnly?: boolean;
    predefinedDateOptions?: DateRangeOption[];
    anchorDate?: DateTime;
  };
  const {
    value: endValue,
    reset: resetEnd,
    searchParam: { toUrlString: endTo, defaultValue: endDefault },
  } = useSearchParam<DateTime>(endParamName);

  const isDefault = useMemo(
    () =>
      startTo(startValue) === startTo(startDefault) &&
      endTo(endValue) === endTo(endDefault),
    [endDefault, endTo, endValue, startDefault, startTo, startValue],
  );

  const displayValue = useMemo(() => {
    if (presetsOnly && predefinedDateOptions?.length) {
      const ranges = makeDateRanges(anchorDate);
      const allowed = Object.fromEntries(
        predefinedDateOptions
          .map(opt => [opt.id, ranges[opt.id]])
          .filter(([, range]) => range != null),
      );
      const matchedId = getDateRangeName([startValue, endValue], allowed);
      const matched = predefinedDateOptions.find(opt => opt.id === matchedId);
      if (matched) return matched.label;
    }
    return getFormattedDate([startValue, endValue]);
  }, [presetsOnly, predefinedDateOptions, anchorDate, startValue, endValue]);

  const reset = () => {
    resetStart();
    resetEnd();
  };

  return (
    <FilterChip
      openFilterDrawer={openFilterDrawer}
      value={displayValue}
      reset={isDefault || (clearable != null && !clearable) ? undefined : reset}
      active={!!startValue && !!endValue}
      name={showName ? name : undefined}
    />
  );
};

export default DateRangeFilterChip;
